import { FC, useState, useCallback } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import Card from '@ui/Card';
import CardContent from '@material-ui/core/CardContent';
import CardActionArea from '@material-ui/core/CardActionArea';
import CardActions from '@material-ui/core/CardActions';
import Button from '@material-ui/core/Button';
import CardMedia from '@material-ui/core/CardMedia';
import Typography from '@ui/Typography';
import CardHeader from '@material-ui/core/CardHeader';
import IconButton from '@material-ui/core/IconButton';
import Info from '@material-ui/icons/Info';
import EuroOutlinedIcon from '@material-ui/icons/EuroOutlined';
import EcoIcon from '@material-ui/icons/Eco';
import Modal from '@ui/Modal';
import ReplyOutlinedIcon from '@material-ui/icons/ReplyOutlined';
import styled from '@ui/styles/styled';
import StyledButton from '@ui/StyledButton';
import { IStyledButtonModes } from '@ui/StyledButton/types';
import RenderDelayer from '@ui/RenderDelayer';
import MaterialPriceIndicatorId from '../../../constants/MaterialPriceIndicatorId';
import MaterialEcoIndicatorId from '../../../constants/MaterialEcoIndicatorId';
import { ActionId } from '../../../constants/Analytics';
import firebaseService from '../../../services/Firebase';
import LazyImage from '../LazyImage';
import Slider from '../Slider';
import IBoxCompositionProperties from './types';
import {
    boxCompositionSubtitleStyle,
    boxCompositionMediaStyle,
    boxCompositionMediaCol2Style,
    boxCompositionCardStyle,
    boxCompositionTitleStyle,
    boxCompositionCardCol2Style,
    boxCompositionCardContentStyle,
    boxCompositionCardActionAreaStyle,
    wrapperBoxInfoIconStyle,
    boxInfoIconStyle,
    wrapperBoxEuroIconStyle,
    modalContainerStyle,
    modalCardStyle,
    getStyledHeaderActionStyle,
    indicatorIconsStyle,
    indicatorEcoIconsActiveStyle,
    indicatorPriceIconsActiveStyle,
    moreInfoButtonStyle,
    moreInfoUndoStyle,
    moreInfoLabelStyle,
    moreInfoCardContentStyle,
    modalContainerStyleFullHeight,
    modalCardStyleFullHeight,
    disabledLabelStyle,
} from './styles';

const StyledCardHeader = styled(CardHeader)(getStyledHeaderActionStyle);
const StyledIconButton = styled(IconButton)(wrapperBoxInfoIconStyle);

const BoxComposition: FC<IBoxCompositionProperties> = ({
    children,
    source,
    labelId,
    subtitleLabelId,
    onClick,
    col2,
    transparent,
    index,
    imagesURLs,
    delay,
    forwardedReference,
    description,
    priceIndicator,
    materialEcoIndicator,
    moreInfoLabelId,
    disabled,
    disabledLabel,
    labelCentred,
}) => {
    const intl = useIntl();
    const [open, setOpen] = useState(false);

    const handleOpen = useCallback(async (event) => {
        event.stopPropagation();
        setOpen(true);
        (await firebaseService.getInstance()).trackAction(ActionId.OPEN_SLIDER, { id: labelId });
    }, [setOpen, labelId]);

    const handleClose = useCallback(async () => {
        setOpen(false);
        (await firebaseService.getInstance()).trackAction(ActionId.CLOSE_SLIDER, { id: labelId });
    }, [setOpen, labelId]);

    return (
        <>
            {(description || imagesURLs) && (
                <Modal
                    aria-labelledby="modal-title"
                    aria-describedby="modal-description"
                    open={open}
                    onClose={handleClose}
                    containerStyle={description && !imagesURLs ? modalContainerStyle : (window.matchMedia('(max-width: 600px)').matches ? modalContainerStyleFullHeight : modalContainerStyle)}
                    cardStyle={description && !imagesURLs ? modalCardStyle : (window.matchMedia('(max-width: 600px)').matches ? modalCardStyleFullHeight : modalCardStyle)}
                >
                    <Slider description={description} sources={imagesURLs} />
                </Modal>
            )}

            <RenderDelayer delay={delay} index={index} grow>
                <Card
                    style={{
                        ...(col2 ? boxCompositionCardCol2Style : boxCompositionCardStyle),
                        ...(transparent && { backgroundColor: 'transparent' }),
                    }}
                    ref={forwardedReference}
                    elevation={0}
                >
                    <CardActionArea
                        onClick={onClick}
                        disabled={disabled}
                        style={{
                            ...boxCompositionCardActionAreaStyle,
                            ...(disabled && { opacity: 0.3 }),
                        }}
                    >
                        {(description || imagesURLs) && (
                            <StyledIconButton onClick={handleOpen} disabled={disabled} aria-label="settings">
                                <Info style={boxInfoIconStyle} />
                            </StyledIconButton>
                        )}

                        <CardContent style={boxCompositionCardContentStyle}>
                            {source && (
                                <CardMedia
                                    title={intl.formatMessage({ id: labelId })}
                                    style={{
                                        ...(col2 ? boxCompositionMediaCol2Style : boxCompositionMediaStyle),
                                        ...(transparent && { backgroundColor: 'transparent' }),
                                    }}
                                >
                                    <LazyImage src={source} alt={intl.formatMessage({ id: labelId })} />
                                </CardMedia>
                            )}

                            {children && (
                                <div
                                    style={{
                                        ...(col2 ? boxCompositionMediaCol2Style : boxCompositionMediaStyle),
                                        ...(transparent && { backgroundColor: 'transparent' }),
                                    }}
                                >
                                    {children}
                                </div>
                            )}
                        </CardContent>

                        {(labelId || subtitleLabelId || description || imagesURLs) && (
                            <StyledCardHeader
                                {...((priceIndicator || materialEcoIndicator) && {
                                    action: (
                                        <>
                                            {materialEcoIndicator && (
                                                <Typography variantMapping={{ caption: 'span' }} variant="caption">
                                                    <EcoIcon style={indicatorEcoIconsActiveStyle} />
                                                    <EcoIcon style={MaterialEcoIndicatorId.Good === materialEcoIndicator || MaterialEcoIndicatorId.Regular === materialEcoIndicator || MaterialEcoIndicatorId.Bad === materialEcoIndicator ? indicatorEcoIconsActiveStyle : indicatorIconsStyle} />
                                                    <EcoIcon style={MaterialEcoIndicatorId.Good === materialEcoIndicator || MaterialEcoIndicatorId.Regular === materialEcoIndicator ? indicatorEcoIconsActiveStyle : indicatorIconsStyle} />
                                                    <EcoIcon style={MaterialEcoIndicatorId.Good === materialEcoIndicator ? indicatorEcoIconsActiveStyle : indicatorIconsStyle} />
                                                </Typography>
                                            )}
                                            {priceIndicator && (
                                                <Typography variantMapping={{ caption: 'span' }} variant="caption" style={wrapperBoxEuroIconStyle}>
                                                    <EuroOutlinedIcon style={indicatorPriceIconsActiveStyle} />
                                                    <EuroOutlinedIcon style={MaterialPriceIndicatorId.VeryExpensive === priceIndicator || MaterialPriceIndicatorId.Expensive === priceIndicator || MaterialPriceIndicatorId.Regular === priceIndicator ? indicatorPriceIconsActiveStyle : indicatorIconsStyle} />
                                                    <EuroOutlinedIcon style={MaterialPriceIndicatorId.VeryExpensive === priceIndicator || MaterialPriceIndicatorId.Expensive === priceIndicator ? indicatorPriceIconsActiveStyle : indicatorIconsStyle} />
                                                    <EuroOutlinedIcon style={MaterialPriceIndicatorId.VeryExpensive === priceIndicator ? indicatorPriceIconsActiveStyle : indicatorIconsStyle} />
                                                </Typography>
                                            )}
                                        </>
                                    ),
                                })}
                                {...(labelId && {
                                    title: (
                                        <Typography style={{ ...boxCompositionTitleStyle, ...(labelCentred && { textAlign: 'center' }) }} variantMapping={{ body2: 'h5' }} variant="body2">
                                            <FormattedMessage id={labelId} />
                                        </Typography>
                                    ),
                                })}
                                {...(subtitleLabelId && intl.messages[subtitleLabelId] && {
                                    subheader: (
                                        <Typography variantMapping={{ caption: 'h6' }} variant="caption" style={boxCompositionSubtitleStyle}>
                                            <FormattedMessage id={subtitleLabelId} />
                                        </Typography>
                                    ),
                                })}
                            />
                        )}
                    </CardActionArea>

                    {disabledLabel && disabled && (
                        <StyledButton aria-label="disabled" style={disabledLabelStyle} mode={IStyledButtonModes.Small}>
                            <small><FormattedMessage id={disabledLabel} /></small>
                        </StyledButton>
                    )}
                </Card>
            </RenderDelayer>

            {moreInfoLabelId && (
                <RenderDelayer delay={delay} index={index} grow>
                    <Card
                        style={boxCompositionCardCol2Style}
                        elevation={0}
                    >
                        <CardContent style={moreInfoCardContentStyle}>
                            <ReplyOutlinedIcon style={moreInfoUndoStyle} />
                            {moreInfoLabelId && (
                                <Typography variantMapping={{ caption: 'p' }} variant="caption" style={moreInfoLabelStyle}>
                                    <FormattedMessage id={moreInfoLabelId} />
                                </Typography>
                            )}
                        </CardContent>

                        <CardActions>
                            {(description || imagesURLs) && (
                                <Button onClick={handleOpen} disabled={disabled} style={moreInfoButtonStyle} size="small">
                                    <FormattedMessage id="moreInfo" />
                                </Button>
                            )}
                        </CardActions>
                    </Card>
                </RenderDelayer>
            )}
        </>
    );
};

export default BoxComposition;
